import React, { useState } from 'react';
import { Element } from 'react-scroll';
import styled from '@emotion/styled';
import useScrollToFooter from '../../hooks/useScrollToFooter';
import LogoWhite from '../../assets/svg/header/logo-white.svg';
import LogoColor from '../../assets/svg/header/logo-color.svg';
import { useLang } from '../../context/LanguageContext';
import { t } from '../../translations';

const Section = styled.section`
  background: #F8F8F8;
  border-top: 1px solid #E8E8E8;
  padding: 100px 50px 0;

  @media (max-width: 640px) {
    padding: 64px 20px 0;
  }
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const CTABlock = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  padding-bottom: 80px;
  border-bottom: 1px solid #E8E8E8;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 48px;
  }
`;

const CTALeft = styled.div``;

const Label = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #D97757;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin: 0 0 16px;
`;

const Title = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(28px, 4vw, 44px);
  font-weight: 700;
  color: #0A0A0A;
  letter-spacing: -1px;
  line-height: 1.15;
  margin: 0 0 16px;
`;

const Subtitle = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  color: #666;
  line-height: 1.7;
  margin: 0 0 40px;
  max-width: 420px;
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const InfoDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #D97757;
  flex-shrink: 0;
`;

const InfoText = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #888;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const FormLabel = styled.label`
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  display: block;
  margin-bottom: 6px;
`;

const FormField = styled.div``;

const Input = styled.input`
  width: 100%;
  background: #FFFFFF;
  border: 1px solid ${({ error }) => error ? '#E05252' : '#E0E0E0'};
  border-radius: 10px;
  padding: 12px 16px;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #111;
  outline: none;
  transition: border-color 200ms ease;
  box-sizing: border-box;

  &::placeholder {
    color: #BBBBBB;
  }

  &:focus {
    border-color: ${({ error }) => error ? '#E05252' : 'rgba(217, 119, 87, 0.5)'};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  background: #FFFFFF;
  border: 1px solid ${({ error }) => error ? '#E05252' : '#E0E0E0'};
  border-radius: 10px;
  padding: 12px 16px;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #111;
  outline: none;
  transition: border-color 200ms ease;
  box-sizing: border-box;
  resize: vertical;
  min-height: 120px;

  &::placeholder {
    color: #BBBBBB;
  }

  &:focus {
    border-color: ${({ error }) => error ? '#E05252' : 'rgba(217, 119, 87, 0.5)'};
  }
`;

const ErrorMsg = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  color: #E05252;
  margin-top: 4px;
  display: block;
`;

const HoneypotField = styled.div`
  position: absolute;
  left: -9999px;
  top: -9999px;
  opacity: 0;
  pointer-events: none;
  tab-index: -1;
  aria-hidden: true;
`;

const SuccessBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 12px;
  padding: 40px;
  background: rgba(125, 211, 168, 0.06);
  border: 1px solid rgba(125, 211, 168, 0.2);
  border-radius: 16px;
  height: 100%;
  min-height: 240px;
`;

const SuccessIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(125, 211, 168, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
`;

const SuccessTitle = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #111;
  margin: 0;
`;

const SuccessText = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #888;
  margin: 0;
  line-height: 1.6;
`;

const SubmitButton = styled.button`
  align-self: flex-start;
  margin-top: 16px;
  padding: 18px 48px;

  @media (max-width: 640px) {
    width: 100%;
    align-self: stretch;
  }
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #FFFFFF;
  background: #D97757;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: background 200ms ease, transform 150ms ease;

  &:hover {
    background: #E8895F;
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const Footer = styled.footer`
  padding: 48px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
    padding: 32px 0;
  }
`;

const FooterLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FooterBrand = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #111;
  letter-spacing: -1px;
`;

const FooterCopy = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  color: #999;
`;

const FooterLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  @media (max-width: 640px) {
    margin-left: -10px;
  }
`;

const FooterLink = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 10px 16px;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #666;
  text-decoration: none;
  border-radius: 6px;
  background: none;
  border: none;
  cursor: pointer;
  transition: color 200ms ease, background 200ms ease;

  &:hover {
    color: #111;
    background: rgba(0,0,0,0.04);
  }
`;

const Contact = () => {
  const scrollToFooter = useScrollToFooter();
  const { lang } = useLang();
  const tr = t[lang].contact;
  const navTr = t[lang].nav;
  const [honeypot, setHoneypot] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [fields, setFields] = useState({ name: '', email: '', phone: '', message: '' });
  const [errors, setErrors] = useState({});

  const scrollToServices = () => {
    const el = document.getElementById('services');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const validate = () => {
    const e = {};
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRe = /^[\d\s\+\-\(\)]{7,20}$/;
    if (!fields.name.trim()) e.name = lang === 'pl' ? 'Imię jest wymagane' : 'Name is required';
    if (!fields.email.trim()) e.email = lang === 'pl' ? 'Email jest wymagany' : 'Email is required';
    else if (!emailRe.test(fields.email)) e.email = lang === 'pl' ? 'Nieprawidłowy adres email' : 'Invalid email address';
    if (!fields.phone.trim()) e.phone = lang === 'pl' ? 'Telefon jest wymagany' : 'Phone is required';
    else if (!phoneRe.test(fields.phone)) e.phone = lang === 'pl' ? 'Nieprawidłowy numer telefonu' : 'Invalid phone number';
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFields(f => ({ ...f, [name]: value }));
    if (errors[name]) setErrors(err => ({ ...err, [name]: undefined }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (honeypot) return;
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSubmitted(true);
  };

  return (
  <Element name="contact">
    <Section>
      <Container>
        <CTABlock>
          <CTALeft>
            <Label>{tr.label}</Label>
            <Title>{tr.title}</Title>
            <Subtitle>{tr.subtitle}</Subtitle>
            <InfoList>
              <InfoItem>
                <InfoDot />
                <InfoText as="a" href="mailto:hello@modernware.io" style={{ textDecoration: 'none', color: 'inherit' }}>hello@modernware.io</InfoText>
              </InfoItem>
              <InfoItem>
                <InfoDot />
                <InfoText as="a" href="tel:+48798094110" style={{ textDecoration: 'none', color: 'inherit' }}>+48 798 094 110</InfoText>
              </InfoItem>
              <InfoItem>
                <InfoDot />
                <InfoText as="a" href="https://www.google.com/maps/place/Coders+Lab+Katowice/@50.250865,19.014181,62m/data=!3m1!1e3!4m15!1m8!3m7!1s0x4716ce457cba15ff:0x872f4bed4822d3bd!2sTadeusza+Ko%C5%9Bciuszki+55,+40-056+Katowice!3b1!8m2!3d50.2510222!4d19.014186!16s%2Fg%2F11rgdlmqkb!3m5!1s0x4716cf7084c9c4c1:0xf3e6547480f2bd12!8m2!3d50.251046!4d19.0141024!16s%2Fg%2F11h1fymk5j?entry=ttu&g_ep=EgoyMDI2MDcyMi4wIKXMDSoASAFQAw%3D%3D" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>Kościuszki 55, 40-174 Katowice</InfoText>
              </InfoItem>
            </InfoList>
          </CTALeft>

          {submitted ? (
            <SuccessBox>
              <SuccessIcon>✓</SuccessIcon>
              <SuccessTitle>{lang === 'pl' ? 'Wiadomość wysłana!' : 'Message sent!'}</SuccessTitle>
              <SuccessText>{lang === 'pl' ? 'Dziękujemy za kontakt. Odezwiemy się do Ciebie wkrótce.' : 'Thank you for reaching out. We\'ll get back to you shortly.'}</SuccessText>
            </SuccessBox>
          ) : (
          <Form
            id="contact-form"
            name="contact-form"
            onSubmit={handleSubmit}
            noValidate
          >
            <HoneypotField aria-hidden="true">
              <input
                type="text"
                name="website"
                tabIndex="-1"
                autoComplete="off"
                value={honeypot}
                onChange={e => setHoneypot(e.target.value)}
              />
            </HoneypotField>

            <FormField>
              <FormLabel htmlFor="name">{tr.form.name}</FormLabel>
              <Input type="text" id="name" name="name" placeholder={tr.form.namePlaceholder} maxLength="256" value={fields.name} onChange={handleChange} error={errors.name} />
              {errors.name && <ErrorMsg>{errors.name}</ErrorMsg>}
            </FormField>
            <FormField>
              <FormLabel htmlFor="email">{tr.form.email}</FormLabel>
              <Input type="text" id="email" name="email" placeholder={tr.form.emailPlaceholder} maxLength="256" value={fields.email} onChange={handleChange} error={errors.email} />
              {errors.email && <ErrorMsg>{errors.email}</ErrorMsg>}
            </FormField>
            <FormField>
              <FormLabel htmlFor="phone">{tr.form.phone}</FormLabel>
              <Input type="text" id="phone" name="phone" placeholder={tr.form.phonePlaceholder} maxLength="256" value={fields.phone} onChange={handleChange} error={errors.phone} />
              {errors.phone && <ErrorMsg>{errors.phone}</ErrorMsg>}
            </FormField>
            <FormField>
              <FormLabel htmlFor="message">{tr.form.message}</FormLabel>
              <Textarea id="message" name="message" placeholder={tr.form.messagePlaceholder} maxLength="1000" value={fields.message} onChange={handleChange} error={errors.message} />
              {errors.message && <ErrorMsg>{errors.message}</ErrorMsg>}
            </FormField>
            <SubmitButton type="submit">{tr.form.submit}</SubmitButton>
          </Form>
          )}
        </CTABlock>

        <Footer>
          <FooterLeft>
            <FooterBrand><img src={LogoColor} height="28" alt="Modernware" /></FooterBrand>
            <FooterCopy>© {new Date().getFullYear()} Modernware. {tr.footer.copy}</FooterCopy>
            <FooterCopy>NIP: 6252452555 · ul. Kościuszki 55, 40-174 Katowice</FooterCopy>
          </FooterLeft>
          <FooterLinks>
            <FooterLink as="button" onClick={scrollToServices}>{navTr.services}</FooterLink>
            <FooterLink as="button" onClick={() => scrollToFooter()}>{navTr.contact}</FooterLink>
            <FooterLink href="/polityka-prywatnosci">{lang === 'pl' ? 'Polityka prywatności' : 'Privacy Policy'}</FooterLink>
          </FooterLinks>
        </Footer>
      </Container>
    </Section>
  </Element>
  );
};

export default Contact;
